import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Login from "./components/Login/index.tsx";
import Sidebar from "./coreComponents/SideBar/SideBar.tsx";
import TopBar from "./coreComponents/TopBar/TopBar.tsx";
import Driver from "./components/Driver/index.tsx";
import Rides from "./components/Rides/index.tsx";
import CreateDriver from "./components/Driver/CreateDriver.tsx";
import { login } from "./services/AuthService.ts";
import User from "./components/User/index.tsx";
import Vehicle from "./components/Vehicle/index.tsx";
import ActiveDriver from "./components/ActiveDrivers/index.tsx";
import "./App.css";
import Dashboard from "./components/Dashboard/index.tsx";
import TestNumbers from "./components/TestNumbers/index.tsx";
import CreateTestNumber from "./components/TestNumbers/createTestNumber.tsx";
import FareManagement from "./components/FareManagement/index.tsx";
import DriverToVerify from "./components/DriverVerification/PendingDriver.tsx";
import SupportRequests from "./components/SupportRequest/index.tsx";
import Notifications from "./components/Notifications/index.tsx";
import NotificationCreate from "./components/Notifications/NotificationCreate.tsx";
import DriverConfig from "./components/DriverConfig/index.tsx";

const AuthLayout: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showCreateNotification, setShowCreateNotification] = useState(false);

  // Get action button based on route
  const getActionButton = () => {
    const path = location.pathname;
    const buttons: {
      [key: string]: {
        label: string;
        onClick: () => void;
        variant?: "primary" | "secondary";
      };
    } = {
      "/driver": {
        label: "Create Driver",
        onClick: () => navigate("/create-driver"),
        variant: "primary",
      },
      "/test-numbers": {
        label: "Create Test Number",
        onClick: () => navigate("/create-test-number"),
        variant: "primary",
      },
      "/fare-management": {
        label: "Create Fare",
        onClick: () => {},
        variant: "primary",
      },
      "/user": {
        label: "Create User",
        onClick: () => {},
        variant: "primary",
      },
      "/notifications": {
        label: "Create Notification",
        onClick: () => navigate("/notifications/create"),
        variant: "primary",
      },
      "/driver-config": {
        label: "Update Config",
        onClick: () => navigate("/driver-config/update"),
        variant: "primary",
      },
    };
    return buttons[path];
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <TopBar onLogout={onLogout} actionButton={getActionButton()} />
      <div style={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <Sidebar />
        <div
          style={{
            flexGrow: 1,
            overflow: "auto",
            padding: "0",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/driver" element={<Driver />} />
            <Route path="/create-driver" element={<CreateDriver />} />
            <Route path="/user" element={<User />} />
            <Route path="/vehicle" element={<Vehicle />} />
            <Route path="/active-driver" element={<ActiveDriver />} />
            <Route path="/test-numbers" element={<TestNumbers />} />
            <Route path="/create-test-number" element={<CreateTestNumber />} />
            <Route path="/fare-management" element={<FareManagement />} />
            <Route path="/support-requests" element={<SupportRequests />} />
            <Route path="/driver-verification" element={<DriverToVerify />} />
            <Route path="/rides" element={<Rides />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route
              path="/notifications/create"
              element={<NotificationCreate />}
            />
            <Route path="/driver-config" element={<DriverConfig />} />
            <Route
              path="/driver-config/update"
              element={
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 24,
                    padding: 32,
                  }}
                >
                  <button
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 28,
                      marginRight: 16,
                    }}
                    onClick={() => window.history.back()}
                    aria-label="Back"
                  >
                    &#8592;
                  </button>
                  <div style={{ flex: 1 }}>
                    <h2>Update Config</h2>
                    <React.Suspense fallback={<div>Loading...</div>}>
                      {React.createElement(
                        require("./components/DriverConfig/UpdateConfig.tsx")
                          .default
                      )}
                    </React.Suspense>
                  </div>
                </div>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute: React.FC<{
  element: JSX.Element;
  isAuthenticated: boolean;
}> = ({ element, isAuthenticated }) => {
  return isAuthenticated ? element : <Navigate to="/login" replace />;
};

const AuthChecker: React.FC<{
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
      navigate("/login");
    }
  }, [navigate, setIsAuthenticated]);

  return null;
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const handleLogin = async (username: string, password: string) => {
    const response = await login(username, password);
    console.log(response);
    const { accessToken, refreshToken } = response.data;
    if (response.data) {
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      setIsAuthenticated(true);
      return Promise.resolve();
    }
    return Promise.reject(new Error("Invalid credentials"));
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setIsAuthenticated(false);
  };

  return (
    <Router>
      <AuthChecker setIsAuthenticated={setIsAuthenticated} />
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute
              element={<AuthLayout onLogout={handleLogout} />}
              isAuthenticated={isAuthenticated}
            />
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
